import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://revops.digitais.ai';
const DEFAULT_IMAGE = 'https://revops.digitais.ai/og-default.png';

interface SEOHeadProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  imageAlt?: string;
  noIndex?: boolean;
  keywords?: string;
  canonicalOverride?: string;
  article?: {
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
    section?: string;
  };
}

export function SEOHead({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  imageAlt,
  noIndex = false,
  keywords,
  canonicalOverride,
  article,
}: SEOHeadProps) {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const effectivePath = path || pathname;
  const isPt = i18n.language?.startsWith('pt');
  const lang = isPt ? 'pt-BR' : 'en';
  const ogLocale = isPt ? 'pt_BR' : 'en_US';
  const ogLocaleAlt = isPt ? 'en_US' : 'pt_BR';
  const url = `${BASE_URL}${effectivePath}`;
  const canonical = canonicalOverride || url;

  // Force twitter:image via DOM to prevent CDN overwrite
  useEffect(() => {
    const selector = 'meta[name="twitter:image"]';
    document.querySelectorAll(selector).forEach(el => {
      if (!el.hasAttribute('data-rh')) {
        el.remove();
      }
    });
    if (!document.querySelector(`${selector}[data-rh]`)) {
      const meta = document.createElement('meta');
      meta.name = 'twitter:image';
      meta.content = image;
      meta.setAttribute('data-rh', 'true');
      document.head.appendChild(meta);
    }
  }, [image]);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="author" content="RevOps AI" />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Open Graph */}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt || title} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content="RevOps AI" />

      {/* Article meta */}
      {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
      {article?.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
      {article?.author && <meta property="article:author" content={article.author} />}
      {article?.section && <meta property="article:section" content={article.section} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@RevOpsAI" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Canonical */}
      <link rel="canonical" href={canonical} />
    </Helmet>
  );
}
